import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment-timezone';
import { ExcelFile, ExcelSheet, ExcelColumn } from 'react-xlsx-wrapper';
import CommonHeader from '../../../util_components/common_header';
import { get_api as getApi, post_api as postApi } from '../../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../../utils/urls';
import * as actions from '../../../../redux/action_creators';
import InputsPanel from './InputsPanel';
import { FiltersCard, StudentSummary, TimingsStrip, TopCards, WarningsBanner } from './Summary';
import { PoolTable, RankedTable } from './Tables';
import {
  EMPTY_OVERRIDES,
  buildFunnel,
  buildOverrides,
  buildUrlSearch,
  copyText,
  parseUrlState,
  poolCsvRows,
  rankedCsvRows,
  SLOT_ALIGN_ERROR,
  parseSlot
} from './constants';
import './reco_v2.css';

const PLAYGROUND_PATH = '/v2/recommendations_v2/playground';
const META_PATH = '/v2/recommendations_v2/meta';
const STUDENT_PATH = '/v2/recommendations_v2/student';
const REFRESH_PATH = '/v2/recommendations_v2/internal/refresh_daily_stats';

const DEFAULT_SLOT_TIME = '07:00';

// Accepts either a bare body or a {data: body} envelope.
const unwrap = (res) => {
  const body = res && res.data;
  if (body && body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
    return body.data;
  }
  return body || {};
};

const errorMessage = (e) =>
  (e &&
    e.response &&
    e.response.data &&
    (e.response.data.reason || e.response.data.message || e.response.data.error)) ||
  (e && e.message) ||
  'Request failed';

const RANKED_CSV_COLUMNS = [
  'rank',
  'teacher_id',
  'teacher_uuid',
  'name',
  'gender',
  'goals_matched',
  'matched_goals',
  'total_goals',
  'goal_score_x10',
  'days_available',
  'repeat_available',
  'avail_score_x10',
  'fit_score',
  'finished_trials',
  'points_balance',
  'effective_points_x100',
  'points_decile',
  'retention_score_x100',
  'retention_is_platform_avg',
  'retention_decile',
  'created_date',
  'profile_live_at',
  'live_date',
  'finished_trials_source',
  'finished_trials_source_label',
  'decided_by',
  'decided_by_label',
  'shown_status',
  'shown_status_label',
  'reason'
];

const POOL_CSV_COLUMNS = [
  'teacher_id',
  'teacher_uuid',
  'name',
  'gender',
  'phase_a_passed',
  'phase_b_passed',
  'rank',
  'fit_score',
  'filters',
  'filters_readable'
];

const renderValue = (v) => (v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v));

function RecommendationsPlaygroundV2() {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const [initial] = useState(() => parseUrlState(location.search));
  const initialEmail = initial.email || '';

  const [email, setEmail] = useState(initialEmail);
  const [phaseAOnly, setPhaseAOnly] = useState(initial.phaseAOnly || false);
  const [slotDate, setSlotDate] = useState(initial.slotDate || '');
  const [slotTime, setSlotTime] = useState(initial.slotTime || '');
  const [topN, setTopN] = useState(initial.topN || 3);
  const [writeAudit, setWriteAudit] = useState(initial.writeAudit !== false);
  const [overrides, setOverrides] = useState(initial.overrides || EMPTY_OVERRIDES);
  const [studentContext, setStudentContext] = useState(null);
  const [metaGoals, setMetaGoals] = useState([]);
  const [result, setResult] = useState(null);
  const [resultPhaseAOnly, setResultPhaseAOnly] = useState(false);
  const [resultTopN, setResultTopN] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);

  const notifyError = useCallback(
    (msg) => {
      setError(msg);
      dispatch(actions.set_notification_variable(true, 'error', msg));
    },
    [dispatch]
  );

  useEffect(() => {
    getApi(getApiUrl(META_PATH, false, true), true)
      .then((res) => {
        const meta = unwrap(res);
        setMetaGoals(Array.isArray(meta.goals) ? meta.goals : []);
      })
      .catch((e) => notifyError(`Could not load goal vocabulary: ${errorMessage(e)}`));
  }, [notifyError]);

  const setOverride = useCallback((key, value) => {
    setOverrides((prev) => ({ ...prev, [key]: value }));
  }, []);

  const fetchReco = (body) =>
    postApi(getApiUrl(PLAYGROUND_PATH, false, true), body, true).then(unwrap);

  const fetchStudentContext = (studentEmail) =>
    getApi(
      getApiUrl(`${STUDENT_PATH}?email=${encodeURIComponent(studentEmail)}`, false, true),
      true
    ).then(unwrap);

  const getRecommendations = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      notifyError('Enter a student email');
      return;
    }
    const parsedTopN = parseInt(topN, 10);
    const body = {
      email: trimmedEmail,
      top_n: Number.isNaN(parsedTopN) || parsedTopN < 1 ? 3 : parsedTopN,
      write_audit: writeAudit,
      overrides: buildOverrides(overrides)
    };
    setResult(null);
    setResultPhaseAOnly(false);
    setLoading(true);
    setError('');
    let urlDate = slotDate;
    let urlTime = slotTime;
    try {
      let ctx =
        studentContext && studentContext.lookupEmail === trimmedEmail ? studentContext : null;
      if (!ctx) {
        const student = await fetchStudentContext(trimmedEmail);
        ctx = { ...student, lookupEmail: trimmedEmail };
        setStudentContext(ctx);
      }

      if (!phaseAOnly) {
        const tz = ctx.timezone || 'UTC';
        let date = slotDate;
        let time = slotTime;
        if (!date || !time) {
          date = date || moment.tz(tz).add(1, 'day').format('YYYY-MM-DD');
          time = time || DEFAULT_SLOT_TIME;
          setSlotDate(date);
          setSlotTime(time);
        }
        const slot = parseSlot(date, time, tz);
        if (!slot) {
          notifyError('Invalid slot date/time');
          return;
        }
        if (slot.unix() % 1800 !== 0) {
          notifyError(SLOT_ALIGN_ERROR);
          return;
        }
        body.slot_start_epoch = slot.unix();
        urlDate = date;
        urlTime = time;
      }

      history.replace({
        pathname: location.pathname,
        search: buildUrlSearch({
          email: trimmedEmail,
          slotDate: urlDate,
          slotTime: urlTime,
          topN: body.top_n,
          phaseAOnly,
          writeAudit,
          overrides
        })
      });

      const data = await fetchReco(body);
      setResult(data);
      setResultPhaseAOnly(phaseAOnly);
      setResultTopN(body.top_n);
    } catch (e) {
      notifyError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const autoRan = useRef(false);
  useEffect(() => {
    if (!autoRan.current && initialEmail) {
      autoRan.current = true;
      getRecommendations();
    }
  });

  const copyLink = () => {
    copyText(window.location.href)
      .then(() => dispatch(actions.set_notification_variable(true, 'success', 'Link copied')))
      .catch(() => notifyError('Could not copy the link; copy it from the address bar'));
  };

  const refreshDailyStats = () => {
    setRefreshing(true);
    setRefreshResult(null);
    postApi(getApiUrl(REFRESH_PATH, false, true), {}, true)
      .then((res) => setRefreshResult(unwrap(res)))
      .catch((e) => {
        if (e && e.response && e.response.status === 501) {
          dispatch(
            actions.set_notification_variable(true, 'success', 'Daily stats batch not deployed yet')
          );
          return;
        }
        notifyError(`Refresh daily stats failed: ${errorMessage(e)}`);
      })
      .finally(() => setRefreshing(false));
  };

  const ranked = (result && result.ranked) || [];
  const pool = (result && result.pool) || [];
  const shown = (result && result.shown) || [];
  const filters = (result && result.filters) || {};
  const funnel = result
    ? buildFunnel({
        filters,
        pool,
        slot: resultPhaseAOnly ? null : result.slot,
        shownCount: shown.length,
        topN: result.top_n ?? resultTopN
      })
    : null;
  const activeContext =
    studentContext && studentContext.lookupEmail === email.trim() ? studentContext : null;

  return (
    <CommonHeader loading={loading} show_back title="Teacher Recommendations Playground V2">
      <div className="rv2-page">
        <InputsPanel
          email={email}
          setEmail={setEmail}
          phaseAOnly={phaseAOnly}
          setPhaseAOnly={setPhaseAOnly}
          slotDate={slotDate}
          setSlotDate={setSlotDate}
          slotTime={slotTime}
          setSlotTime={setSlotTime}
          timezone={(activeContext && activeContext.timezone) || ''}
          timezoneIsFallback={!!(activeContext && activeContext.timezone_is_fallback)}
          slotLabel={(result && result.slot && result.slot.student_local) || ''}
          topN={topN}
          setTopN={setTopN}
          writeAudit={writeAudit}
          setWriteAudit={setWriteAudit}
          overrides={overrides}
          setOverride={setOverride}
          metaGoals={metaGoals}
          studentGoals={
            activeContext
              ? (activeContext.goals || []).map((g) => g.display_name).filter(Boolean)
              : []
          }
        />

        <div className="rv2-actions">
          <button type="button" onClick={getRecommendations} disabled={loading}>
            {loading ? 'Loading…' : 'Get Recommendations'}
          </button>
          <button type="button" onClick={refreshDailyStats} disabled={refreshing}>
            {refreshing ? 'Refreshing daily stats…' : 'Refresh daily stats'}
          </button>
          <button type="button" onClick={copyLink}>
            Copy link
          </button>
          {result ? (
            <ExcelFile
              element={<button type="button">Download CSV</button>}
              filename={`recommendations_v2_${result.student?.email || ''}_${result.request_id || ''}`}
            >
              <ExcelSheet data={rankedCsvRows(ranked)} name="Ranked">
                {RANKED_CSV_COLUMNS.map((c) => (
                  <ExcelColumn key={c} label={c} value={c} />
                ))}
              </ExcelSheet>
              <ExcelSheet data={poolCsvRows(pool)} name="Pool">
                {POOL_CSV_COLUMNS.map((c) => (
                  <ExcelColumn key={c} label={c} value={c} />
                ))}
              </ExcelSheet>
            </ExcelFile>
          ) : null}
        </div>

        {refreshResult ? (
          <div className="rv2-card">
            <h3>Daily stats refreshed</h3>
            <dl className="rv2-kv">
              {Object.entries(refreshResult).map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>{renderValue(v)}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}

        {error ? <div className="rv2-error">{error}</div> : null}

        {result ? (
          <>
            <WarningsBanner warnings={result.warnings || []} />
            <TimingsStrip timings={result.timings_ms} />
            {result.student ? (
              <StudentSummary
                student={result.student}
                slot={result.slot}
                phaseAOnly={resultPhaseAOnly}
              />
            ) : null}
            <FiltersCard
              filters={filters}
              funnel={funnel}
              meta={{
                request_id: result.request_id,
                algo_version: result.algo_version,
                random_seed: result.random_seed,
                generated_at: result.generated_at
              }}
            />
            {resultPhaseAOnly ? (
              <div className="rv2-hint" style={{ marginBottom: 12 }}>
                Phase A only: no slot was sent, so availability scores are 0 and Phase B did not
                run.
              </div>
            ) : null}
            <TopCards shown={shown} />
            <RankedTable ranked={ranked} />
            <PoolTable pool={pool} relaxed={filters.relaxed || []} />
          </>
        ) : null}
      </div>
    </CommonHeader>
  );
}

export default RecommendationsPlaygroundV2;
